/**
 * SUB-3.12 · NFR8. When the scope narrows, say so.
 * SUB-4.10 · NFR8 again, for a scope that is a SET rather than one account.
 *
 * > Given a scope that narrows or cannot be resolved, when the user's next
 * > request is served, then they are told, and the system never silently
 * > returns a subset of what they believe they are looking at.
 *
 * > Given a failure in merged scope, when it degrades to single-account scope,
 * > then the user is told, because a silent narrowing satisfies NFR6 while
 * > violating NFR8.
 *
 * ⚠️ WHY THIS EXISTS AT ALL, AND WHY IT IS NOT A TRIGGER.
 *
 * `get_current_user_tenant_id()` revalidates the bounded session row on every
 * call. Detach a sub-account, archive it, or revoke someone's Agency Admin role,
 * and the very next statement resolves to their HOME account instead. It does
 * that quietly and on purpose: the function is evaluated inside hundreds of RLS
 * policies, and raising there would turn a revoked grant into a site-wide outage
 * for that user rather than a return to their own account.
 *
 * `merged_scope_tenant_ids()` narrows the same way and for the same reason: its
 * own comment says so. Both are correct, and both are why this module has to
 * exist.
 *
 * The consequence is this module's whole reason for being. RLS re-resolves per
 * query; the React tree does not. `TenantContext` caches the resolved id for ten
 * minutes and the tenant row for five, and only refetches when the USER changes.
 * So for up to ten minutes the header can name the client account while every
 * list on the page is being served the agency's own rows. Nothing errors. The
 * page just quietly becomes a different account's page.
 *
 * That is precisely the "silently returns a subset of what they believe they are
 * looking at" NFR8 forbids, and no amount of database work can fix it, because
 * the database is already correct. It is a client-side contract, which is also
 * why the story file warns that it is the AC most likely to be quietly dropped.
 *
 * ⚠️ ONE CLASSIFIER, NOT TWO. SUB-4.10 extends `classifyScope` rather than
 * adding a merged-scope sibling. Two classifiers writing one blocking notice is
 * a race whose loser is a notice that never appears, and the case they would
 * disagree about — the account moved AND the set shrank, which is what entering
 * or leaving merged scope does — is the common one, not the corner.
 *
 * Pure functions, so the rule is testable without a Supabase client or a DOM.
 * `tenantScope.test.ts` is the coverage; `TenantContext` supplies the readings.
 */

export type ScopeStatus =
  /** The account being rendered is the account the server resolves to. */
  | 'ok'
  /** The server resolves to a DIFFERENT account than the one on screen. */
  | 'narrowed'
  /**
   * SUB-4.10. The server is serving FEWER ACCOUNTS than this session asked for,
   * or fewer than it was serving a moment ago.
   *
   * Distinct from `narrowed` because the sentence the user reads is different:
   * nothing moved them to another account, the set they are reading across got
   * smaller underneath them. Both are the silent subset NFR8 forbids; only the
   * copy differs.
   */
  | 'degraded'
  /** The server resolves to no account at all. */
  | 'unresolved'
  /**
   * Not established. The resolver could not be reached, or nothing is rendered
   * yet.
   *
   * ⚠️ THIS IS NOT `ok`, AND COLLAPSING THE TWO IS THE BUG THIS TYPE PREVENTS.
   * `isAgencyAdminResolved` exists in `TenantContext` for the same reason: a
   * field with three states and two values reads a failed lookup as a confident
   * negative. Treat `unknown` the way a gate treats `isLoading`.
   */
  | 'unknown';

export interface ScopeReading {
  /** The account id the UI is currently rendering, from context state. */
  displayedTenantId: string | null | undefined;
  /** What `get_current_user_tenant_id()` answers right now, uncached. */
  resolvedTenantId: string | null | undefined;
  /** False when the resolver call itself failed. A failure is not an answer. */
  resolverReachable: boolean;

  // ---------------------------------------------------------------------------
  // SUB-4.10 · the merged half. Every field below is OPTIONAL, and absent means
  // "nothing is known about merged scope", never "there is none". A caller that
  // supplies none of them gets SUB-3.12's behaviour unchanged, which is what the
  // single-account surfaces still want.
  // ---------------------------------------------------------------------------

  /**
   * Whether this SESSION ASKED for merged scope: the stored `scope_mode` column,
   * reported by `merged_scope_status()` as `requested_scope_mode`.
   *
   * ⚠️ INTENT, NOT OUTCOME, AND THAT IS THE ONLY REASON IT IS HERE. Every other
   * merged-scope field in this app is deliberately the outcome (D27). This one
   * has to be the stored column, because a degradation that happened BEFORE this
   * tab loaded leaves no other trace: the resolver simply answers one account,
   * the client has no previous reading to compare against, and FR43 means the
   * user's last selection was All Accounts and they were never told it stopped
   * being honoured.
   *
   * `undefined` means the server did not say, which happens only when
   * `20260814d` has not been applied. It is treated as "no claim", so a missing
   * migration under-reports rather than nagging every user in the product.
   */
  requestedMergedScope?: boolean;
  /** The accounts the server says are in reach RIGHT NOW. */
  resolvedTenantIds?: readonly string[] | null;
  /**
   * The accounts that were in reach when this page last agreed with the server.
   * Null on the first reading: a tab learning where it is has not narrowed.
   */
  previousTenantIds?: readonly string[] | null;
}

/**
 * Has the set of accounts in reach got SMALLER?
 *
 * Two independent pieces of evidence, and either is enough:
 *
 * 1. **Intent versus outcome.** The session asked for merged scope and the
 *    resolver came back with one account or none. Survives a reload, which is
 *    the case nothing else can see.
 * 2. **A proper subset of the previous reading.** Catches the shrink that keeps
 *    merged scope alive — three accounts down to two — which leaves the stored
 *    mode saying `subtree` and every intent-based test happy while a whole
 *    account's rows quietly leave the list.
 *
 * ⚠️ A SET THAT GREW IS NOT A NARROWING, and a set that merely CHANGED is not
 * one either. Entering merged scope, and a new sub-account being created, both
 * add elements; blocking the page for those would train the user to click
 * through the notice that matters.
 */
function mergedScopeNarrowed(reading: ScopeReading): boolean {
  const now = reading.resolvedTenantIds;

  // The reach is not part of this reading at all. Nothing to compare.
  if (!now) return false;

  // 1 · asked for a set, served one account.
  if (reading.requestedMergedScope === true && now.length <= 1) return true;

  // 2 · served fewer than a moment ago.
  const before = reading.previousTenantIds;
  if (!before || before.length <= 1) return false;

  const current = new Set(now);
  const lost = before.some((id) => !current.has(id));
  return lost;
}

/**
 * Compare what is on screen with what the server says, and name the difference.
 *
 * The order of the tests matters, and each step is here because collapsing it
 * into its neighbour is a real defect:
 *
 * - `unknown` is checked before everything, so a failed round trip can never be
 *   reported as a narrowing (which would nag the user on every flaky request)
 *   nor as `ok` (which is the silent subset).
 * - `unresolved` outranks everything after it: an authenticated user with no
 *   account sees empty lists everywhere, and that is not a scope question.
 * - `narrowed` outranks `degraded` when both are true. "You are in a different
 *   account" strictly dominates "you can see fewer accounts": the first means
 *   the figures on screen belong to somebody else, the second means there are
 *   fewer of them. One notice can be shown, so it is the worse one.
 * - `degraded` is nonetheless tested BEFORE "nothing rendered yet". The merged
 *   reading is server intent against server outcome and does not depend on what
 *   the tree has painted, so deferring it to the render would hide the reload
 *   case, which is the one it exists for.
 */
export function classifyScope(reading: ScopeReading): ScopeStatus {
  if (!reading.resolverReachable) return 'unknown';

  // An authenticated user whose account cannot be resolved. Everything they are
  // shown will be empty, and an empty list is indistinguishable from "no data"
  // unless something says otherwise. This is the second half of NFR8.
  if (reading.resolvedTenantId == null) return 'unresolved';

  if (
    reading.displayedTenantId != null &&
    reading.displayedTenantId !== reading.resolvedTenantId
  ) {
    return 'narrowed';
  }

  if (mergedScopeNarrowed(reading)) return 'degraded';

  // Nothing rendered yet, so there is nothing to contradict.
  if (reading.displayedTenantId == null) return 'unknown';

  return 'ok';
}

export interface ScopeNotice {
  status: Exclude<ScopeStatus, 'ok' | 'unknown'>;
  title: string;
  body: string;
  /** The label on the single action. Every case reloads onto the truth. */
  actionLabel: string;
}

/**
 * SUB-4.10. What the `degraded` sentence needs beyond the two account names.
 *
 * Counts, never the identity of what was lost. Naming the account that left is
 * the same disclosure `scopeNoticeFor` already refuses to make about the reason:
 * after the change the user may no longer be entitled to know that account was
 * ever reachable through them.
 */
export interface MergedScopeReach {
  /** Accounts in reach now. One or zero means merged scope is gone entirely. */
  accountsNow: number;
  /** Accounts in reach a moment ago, or null when there was no prior reading. */
  accountsBefore?: number | null;
}

/**
 * The sentence the user reads.
 *
 * ⚠️ IT NAMES THE ACCOUNT THEY ARE MOVING TO, NEVER WHY. "Your access to X was
 * revoked by Y" would disclose a hierarchy change to someone who, after the
 * change, may no longer be entitled to know one happened. ISO12 and SUB-2.12 are
 * the standing rules; the same non-disclosure idiom is why
 * `set_superadmin_active_tenant` has one refusal for every reason.
 */
export function scopeNoticeFor(
  status: ScopeStatus,
  displayedTenantName: string | null | undefined,
  resolvedTenantName: string | null | undefined,
  reach?: MergedScopeReach | null,
): ScopeNotice | null {
  if (status === 'narrowed') {
    const from = displayedTenantName?.trim() || 'the account you were viewing';
    const to = resolvedTenantName?.trim() || 'your own account';
    return {
      status,
      title: 'You are no longer in this account',
      body:
        `Your access to ${from} has ended, so this page is now showing ${to}. ` +
        'Reload to continue.',
      actionLabel: 'Reload',
    };
  }

  if (status === 'degraded') {
    const accountsNow = reach?.accountsNow ?? 0;
    const accountsBefore = reach?.accountsBefore ?? null;

    // Still merged, just over fewer accounts. The page keeps working and its
    // lists are simply missing rows, which is the harder case to notice and the
    // reason this branch is not folded into the one below.
    if (accountsNow > 1) {
      const was =
        accountsBefore && accountsBefore > accountsNow
          ? `This page was showing ${accountsBefore} accounts and now covers ${accountsNow}. `
          : `This page now covers ${accountsNow} accounts. `;
      return {
        status,
        title: 'Fewer accounts are in view',
        body:
          was +
          'Anything from the accounts that left is missing from every list until you reload. ' +
          'Reload to continue.',
        actionLabel: 'Reload',
      };
    }

    // Fell out of merged scope altogether.
    const to = resolvedTenantName?.trim() || 'one account';
    return {
      status,
      title: 'You are no longer viewing all accounts',
      body:
        `This page is now showing ${to} on its own, so every list is narrower than ` +
        'it appears. Reload to continue.',
      actionLabel: 'Reload',
    };
  }

  if (status === 'unresolved') {
    return {
      status,
      title: 'Your account could not be determined',
      body:
        'Nothing on this page is a complete view while that is true. It is not ' +
        'that there is no data. Reload, and contact your administrator if this ' +
        'keeps happening.',
      actionLabel: 'Reload',
    };
  }

  return null;
}

/**
 * For lists and empty states. An empty result under a broken scope must not be
 * rendered as "nothing here" (NFR8, second sentence).
 */
export function emptyStateIsTrustworthy(status: ScopeStatus): boolean {
  return status === 'ok';
}
